import React, { useState, useEffect, useCallback, memo } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Button,
  Modal,
  Paper,
  Container,
  Breadcrumbs,
  Fade,
  Divider,
  IconButton,
  ListItemIcon,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery
} from "@mui/material";

// アイコン
import HomeIcon from '@mui/icons-material/Home';
import GroupsIcon from '@mui/icons-material/Groups';
import AddIcon from '@mui/icons-material/Add';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FolderIcon from '@mui/icons-material/Folder';
import CloseIcon from '@mui/icons-material/Close';

import Header from "../../components/Header";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const MAX_GROUP_NAME_LENGTH = 50;
const FORBIDDEN_REGEX = /[<>"'`;\\\/*?&|^$()[\]{}:\x00-\x1F\x7F]/;

// メモ化されたグループアイテムコンポーネント
const GroupItem = memo(({ group, onClick }) => (
  <ListItem disablePadding divider>
    <ListItemButton onClick={() => onClick(group.id)}>
      <ListItemIcon>
        <FolderIcon color="primary" />
      </ListItemIcon>
      <ListItemText 
        primary={group.name} 
        primaryTypographyProps={{ fontWeight: 'medium' }}
      />
      <NavigateNextIcon color="action" />
    </ListItemButton>
  </ListItem>
));

export default function GroupList() {
  // 状態管理を統合
  const [state, setState] = useState({
    groups: [],
    loading: true,
    error: null,
    newGroupName: "",
    groupNameError: "",
    modalOpen: false
  });
  
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // 状態から分割代入でアクセス
  const { groups, loading, error, newGroupName, groupNameError, modalOpen } = state;

  // 状態の部分更新ヘルパー関数
  const updateState = useCallback((updates) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  // APIからグループデータを取得
  const fetchGroups = useCallback(async () => {
    const token = sessionStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      updateState({ loading: true });
      
      const res = await axios.get("/api/client_groups", {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      updateState({ 
        groups: res.data,
        loading: false,
        error: null
      });
    } catch (err) {
      updateState({ 
        error: "グループ情報の取得に失敗しました。",
        loading: false 
      });
    }
  }, [navigate, updateState]);

  // 初期データ取得
  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // グループクリック時のハンドラー
  const handleGroupClick = useCallback((groupId) => {
    navigate(`/group-edit/${groupId}`);
  }, [navigate]);

  // 新規グループ作成処理
  const handleCreateGroup = useCallback(async () => {
    const token = sessionStorage.getItem("token");
    
    try {
      updateState({ loading: true });
      
      const res = await axios.post(
        "/api/client_groups/create",
        { name: newGroupName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      updateState({
        groups: [...groups, res.data],
        modalOpen: false,
        newGroupName: "",
        loading: false
      });
    } catch (err) {
      const msg = err.response?.data || "グループ作成に失敗しました。";
      updateState({
        groupNameError: msg,
        loading: false
      });
    }
  }, [groups, newGroupName, updateState]);
     
  // グループ名入力時のバリデーション
  const handleNewGroupNameChange = useCallback((e) => {
    const value = e.target.value;
    let error = "";

    if (value.length > MAX_GROUP_NAME_LENGTH) {
      error = "グループ名は50文字以内で入力してください。";
    } else if (FORBIDDEN_REGEX.test(value)) {
      error = "使用できない文字が含まれています（< > \" など）。";
    } else if (!value.trim()) {
      error = "グループ名を入力してください。";
    }

    updateState({
      newGroupName: value,
      groupNameError: error
    });
  }, [updateState]);

  // モーダルを開く
  const openModal = useCallback(() => {
    updateState({ 
      modalOpen: true,
      newGroupName: "",
      groupNameError: "" 
    });
  }, [updateState]);

  // モーダルを閉じる
  const closeModal = useCallback(() => {
    updateState({ modalOpen: false });
  }, [updateState]);

  return (
    <>
      <Header />
      <Container 
        maxWidth="md" 
        sx={{ 
          mt: 4, 
          mb: 6, 
          px: { xs: 2, sm: 3 },
          // PWAボトムナビゲーション対応：安全マージンを確保
          pb: { xs: 12, sm: 6 }, // モバイルは12（約96px）、デスクトップは6
          // 最小高さを確保してスクロール可能にする
          minHeight: { xs: 'calc(100vh - 200px)', sm: 'auto' }
        }}
      >
        {/* パンくずリスト */}
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />} 
          aria-label="breadcrumb"
          sx={{ mb: 3 }}
        >
          <Typography
            component="button"
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              textDecoration: 'none',
              color: 'inherit',
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit'
            }}
            onClick={() => navigate('/dashboard')}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            ホーム
          </Typography>
          <Typography
            sx={{ display: 'flex', alignItems: 'center', fontWeight: 500 }}
            color="text.primary"
          >
            <GroupsIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            グループ一覧
          </Typography>
        </Breadcrumbs>

        <Fade in={true}>
          <Box>
            {/* ヘッダーセクション */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                グループ一覧
              </Typography>
              <Typography color="text.secondary">
                顧客のグループを作成・管理します
              </Typography>
            </Box>

            <Divider sx={{ mb: 4 }} />

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
            ) : (
              <Card 
                elevation={2} 
                sx={{ 
                  borderRadius: 2,
                  overflow: 'hidden',
                  mb: 4
                }}
              >
                <Box 
                  sx={{ 
                    px: 3, 
                    py: 2, 
                    bgcolor: 'primary.light',
                    color: 'primary.dark',
                    borderBottom: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GroupsIcon />
                    <Typography variant="subtitle1" fontWeight="medium">
                      グループ
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={openModal}
                  >
                    グループを作成
                  </Button>
                </Box>

                <CardContent sx={{ p: 0 }}>
                  {Array.isArray(groups) && groups.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Typography color="text.secondary" gutterBottom>
                        登録されているグループがありません
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={openModal}
                        sx={{ mt: 1 }}
                      >
                        グループを作成
                      </Button>
                    </Box>
                  ) : (
                    <List sx={{ p: 0 }}>
                      {groups.map((group) => (
                        <GroupItem
                          key={group.id}
                          group={group}
                          onClick={handleGroupClick}
                        />
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            )}

            {/* 戻るボタン */}
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/clientList")}
                size="large"
                sx={{ 
                  minWidth: 200,
                  minHeight: { xs: 48, sm: 40 } // タッチしやすい高さ
                }}
              >
                顧客一覧に戻る
              </Button>
            </Box>
          </Box>
        </Fade>
      </Container>

      {/* グループ作成モーダル */}
      <Modal 
        open={modalOpen} 
        onClose={closeModal}
        aria-labelledby="create-group-modal"
      >
        <Paper
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            p: 3,
            width: isMobile ? "90%" : 400,
            borderRadius: 2,
            outline: 'none'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              グループを作成
            </Typography>
            <IconButton size="small" onClick={closeModal}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <TextField
            fullWidth
            label="グループ名"
            value={newGroupName}
            onChange={handleNewGroupNameChange}
            sx={{ 
              mb: 3,
              '& .MuiInputBase-root': {
                minHeight: { xs: 56, sm: 48 } // タッチしやすい高さ
              }
            }}
            error={Boolean(groupNameError)}
            helperText={groupNameError || `${newGroupName.length}/${MAX_GROUP_NAME_LENGTH} 文字`}
          />

          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button 
              variant="outlined" 
              onClick={closeModal}
              sx={{ minHeight: { xs: 48, sm: 40 } }}
            >
              キャンセル
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateGroup}
              disabled={!newGroupName.trim() || Boolean(groupNameError) || loading}
              sx={{ minHeight: { xs: 48, sm: 40 } }}
            >
              作成
            </Button>
          </Box>
        </Paper>
      </Modal>
    </>
  );
}